import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PortalLayout from '@/components/PortalLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/App';
import PdfPreviewDialog from '@/components/PdfPreviewDialog';
import { FileText, ArrowRight, PenNib, Download, Receipt, Eye } from '@phosphor-icons/react';

const QUOT_STATUS_COLORS = { pending: 'bg-yellow-100 text-yellow-700', accepted: 'bg-blue-100 text-blue-700', declined: 'bg-red-100 text-red-700', signed: 'bg-green-100 text-green-700' };
const INV_STATUS_COLORS = { sent: 'bg-blue-100 text-blue-700', unpaid: 'bg-yellow-100 text-yellow-700', partially_paid: 'bg-amber-100 text-amber-800', paid: 'bg-green-100 text-green-700', overdue: 'bg-red-100 text-red-700' };
const money = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

const PortalQuotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [signedContracts, setSignedContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('quotations');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [qRes, scRes, iRes] = await Promise.all([
          api.get('/quotations'), api.get('/signed-contracts'), api.get('/invoices')
        ]);
        setQuotations(qRes.data);
        setSignedContracts(scRes.data);
        setInvoices(iRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return <PortalLayout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div></div></PortalLayout>;

  const actionQuotations = quotations.filter(q => q.status === 'pending' || q.status === 'accepted');

  return (
    <PortalLayout>
      <div className="max-w-4xl mx-auto space-y-8 fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[#0A192F]">Quotations & Invoices</h1>
          <p className="text-sm text-gray-500 mt-1">View your quotations, invoices, and signed agreements</p>
        </div>

        {/* Action Required */}
        {actionQuotations.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-[#0A192F] mb-3">Action Required</h2>
            <div className="space-y-3">
              {actionQuotations.map(q => (
                <Card key={q.quotation_id} data-testid={`portal-quotation-${q.quotation_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <FileText className="w-8 h-8 text-[#4CAF50]" />
                        <div>
                          <p className="font-medium text-[#0A192F]">{q.company_name}</p>
                          <p className="text-sm text-gray-500">{q.description || 'Quotation'}</p>
                          {q.amount && <p className="text-sm font-medium mt-1">R {q.amount?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })} <span className="text-xs font-normal text-gray-500">incl. VAT</span></p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={QUOT_STATUS_COLORS[q.status]}>{q.status === 'accepted' ? 'Awaiting Signature' : q.status}</Badge>
                        <Link to={`/portal/quotations/${q.quotation_id}`}>
                          <Button className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" size="sm">
                            {q.status === 'accepted' ? <><PenNib className="w-4 h-4 mr-1" /> Sign</> : <><ArrowRight className="w-4 h-4 mr-1" /> Review</>}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200">
          <button onClick={() => setTab('quotations')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'quotations' ? 'border-[#4CAF50] text-[#4CAF50]' : 'border-transparent text-gray-500'}`}>Quotations ({quotations.length})</button>
          <button onClick={() => setTab('invoices')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'invoices' ? 'border-[#4CAF50] text-[#4CAF50]' : 'border-transparent text-gray-500'}`}>Invoices ({invoices.length})</button>
          <button onClick={() => setTab('signed')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'signed' ? 'border-[#4CAF50] text-[#4CAF50]' : 'border-transparent text-gray-500'}`}>Signed Agreements ({signedContracts.length})</button>
        </div>

        {/* Quotations Tab */}
        {tab === 'quotations' && (
          <div className="space-y-3">
            {quotations.map(q => (
              <Card key={q.quotation_id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <FileText className="w-6 h-6 text-gray-400" />
                      <div>
                        <p className="font-medium text-sm text-[#0A192F]">{q.description || q.original_filename}</p>
                        <p className="text-xs text-gray-400">{new Date(q.created_at).toLocaleDateString()}{q.amount ? ` — R ${q.amount?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={QUOT_STATUS_COLORS[q.status]}>{q.status}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPreview({ url: q.source === 'generated' ? `/api/quotations/${q.quotation_id}/pdf` : `/api/quotations/${q.quotation_id}/download`, title: 'Quotation', filename: q.source === 'generated' ? `Quotation-${q.quotation_number}.pdf` : q.original_filename })}
                        data-testid={`portal-view-quotation-${q.quotation_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      <Link to={`/portal/quotations/${q.quotation_id}`}><Button variant="ghost" size="sm" className="text-[#4CAF50]">Open</Button></Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {quotations.length === 0 && <Card><CardContent className="p-8 text-center text-gray-500">No quotations available.</CardContent></Card>}
          </div>
        )}

        {/* Invoices Tab */}
        {tab === 'invoices' && (
          <div className="space-y-3">
            {invoices.map(i => {
              const generated = i.source === 'generated';
              const status = i.display_status || i.status;
              const pdfUrl = generated ? `/api/invoices/${i.invoice_id}/pdf` : `/api/invoices/${i.invoice_id}/download`;
              return (
              <Card key={i.invoice_id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Receipt className="w-6 h-6 text-[#3B82F6]" />
                      <div>
                        <p className="font-medium text-sm text-[#0A192F]">{i.invoice_number ? `#${i.invoice_number} — ` : ''}{i.description || i.original_filename}</p>
                        <p className="text-xs text-gray-400">
                          {generated
                            ? `${money(i.total)} incl. VAT${i.due_date ? ` · due ${i.due_date.slice(0, 10)}` : ''}`
                            : `${new Date(i.created_at).toLocaleDateString()}${i.amount ? ` — ${money(i.amount)}` : ''}`}
                        </p>
                        {generated && Number(i.amount_paid) > 0 && status !== 'paid' && (
                          <p className="text-xs text-amber-700 mt-0.5">
                            {money(i.amount_paid)} received · {money(i.balance)} outstanding
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={INV_STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}>{(status || '').replace('_', ' ')}</Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        onClick={() => setPreview({ url: pdfUrl, title: `Invoice ${i.invoice_number || ''}`, filename: generated ? `Invoice-${i.invoice_number}.pdf` : i.original_filename })}
                        data-testid={`portal-view-invoice-${i.invoice_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      <a href={generated ? `${pdfUrl}?download=true` : pdfUrl} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="rounded-none"><Download className="w-4 h-4 mr-1" /> PDF</Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
            {invoices.length === 0 && <Card><CardContent className="p-8 text-center text-gray-500">No invoices available.</CardContent></Card>}
          </div>
        )}

        {/* Signed Agreements Tab */}
        {tab === 'signed' && (
          <div className="space-y-3">
            {signedContracts.map(c => (
              <Card key={c.contract_doc_id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <PenNib className="w-6 h-6 text-[#4CAF50]" />
                      <div>
                        <p className="font-medium text-sm text-[#0A192F]">{c.company_name}</p>
                        <p className="text-xs text-gray-400">Signed: {new Date(c.signature_timestamp).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        onClick={() => setPreview({ url: `/api/signed-contracts/${c.contract_doc_id}/download`, title: 'Signed Agreement', filename: `signed-${c.company_name}.pdf` })}
                        data-testid={`portal-view-signed-${c.contract_doc_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      <a href={`/api/signed-contracts/${c.contract_doc_id}/download`} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="rounded-none"><Download className="w-4 h-4 mr-1" /> PDF</Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {signedContracts.length === 0 && <Card><CardContent className="p-8 text-center text-gray-500">No signed agreements.</CardContent></Card>}
          </div>
        )}

        <PdfPreviewDialog
          open={!!preview}
          onOpenChange={(open) => !open && setPreview(null)}
          url={preview?.url}
          title={preview?.title}
          filename={preview?.filename}
        />
      </div>
    </PortalLayout>
  );
};

export default PortalQuotations;
